import { Button, SimpleGrid, Stack, useDisclosure } from '@chakra-ui/react';
import { LineItemType } from '@pelicargo/types';
import { Show } from '@pelicargo/ui';
import { getPrettyLineItemType, prettyCurrency } from '@pelicargo/utils';
import { useMemo } from 'react';
import { HiChevronDown, HiChevronUp } from 'react-icons/hi2';

import { useCurrentRequest } from '../../hooks/useCurrentRequest';
import { QuoteLineItem } from '../../utils/types';
import { LabeledText } from '../LabeledText';

type MarkupDetailProps = {
  lineItem: QuoteLineItem;
};

export const MarkupDetail = ({ lineItem }: MarkupDetailProps) => {
  const { request } = useCurrentRequest();
  const { isOpen, onToggle } = useDisclosure();
  const markup = useMemo(() => lineItem?.issued_by_markup, [lineItem?.issued_by_markup]);

  const actualCw = useMemo(() => {
    return request?.chargeable_weight || 0;
  }, [request?.chargeable_weight]);

  const isConnected = useMemo(() => {
    return lineItem?.disassociated_at === null;
  }, [lineItem?.disassociated_at]);

  const markupCosts = useMemo(() => {
    return markup?.costs || [];
  }, [markup?.costs]);

  const renderMarkupCost = (cost: any) => {
    const label = cost?.min ? `${cost?.min} (kg)` : 'N';
    const max = cost?.max || Number.MAX_SAFE_INTEGER;
    const isPossible = actualCw < max;
    const isSelected = cost.id === lineItem?.issued_by_tiered_cost_id;

    const opacity = isSelected || isPossible ? 1 : 0.5;
    const possibilityColor = isPossible ? 'success' : 'error';
    const color = isSelected ? possibilityColor : undefined;

    return (
      <LabeledText key={cost.id} label={label} value={prettyCurrency(cost.cost)} color={color} opacity={opacity} />
    );
  };

  if (!markup) return null;

  return (
    <>
      <Stack direction="column" spacing="2">
        <Stack direction="column" spacing={0}>
          <Stack direction="column" spacing={3} p={3} flex={1}>
            <LabeledText
              flex={1}
              label="Status"
              value={isConnected ? 'Connected' : 'No Connection'}
              color={isConnected ? 'success' : 'error'}
            />
          </Stack>
          <Stack direction="column" spacing={3} p={3} flex={1} borderTopWidth={1}>
            <SimpleGrid columns={3} rowGap={3} columnGap={2}>
              <LabeledText flex={1} label="Min Cost" value={prettyCurrency(markup?.min_cost)} />
              {markupCosts?.map(renderMarkupCost)}
            </SimpleGrid>
          </Stack>

          <Show if={isOpen}>
            <SimpleGrid columns={3} rowGap={3} columnGap={2} p={3} borderTopWidth={1}>
              <LabeledText label="ID" value={String(markup?.id || '')} />
              <LabeledText label="Markup Config ID" value={String(markup?.markup_configuration_id || '')} />
              <LabeledText label="Category" value={getPrettyLineItemType(markup?.category as LineItemType)} />
            </SimpleGrid>
          </Show>
        </Stack>
        <Button
          size="sm"
          variant="ghost"
          onClick={onToggle}
          colorScheme="brand"
          alignSelf="center"
          rightIcon={isOpen ? <HiChevronUp /> : <HiChevronDown />}
        >
          {isOpen ? 'Minimize' : 'Expand'}
        </Button>
      </Stack>
    </>
  );
};
